// src/pages/CancellationRefunds.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import { Seo } from "../lib/seo";



const SECTIONS = [
  { id: "summary", label: "Summary" },
  { id: "who", label: "Who this covers" },
  { id: "deposit", label: "Deposits & confirmation" },
  { id: "coolingoff", label: "Cooling-off rights" },
  { id: "cancellations", label: "Your cancellations" },
  { id: "rescheduling", label: "Rescheduling" },
  { id: "changes", label: "Package changes" },
  { id: "ourside", label: "If we cancel / change" },
  { id: "weather", label: "Weather, access & safety" },
  { id: "quality", label: "Quality & remedies" },
  { id: "hire", label: "Hire items & damage" },
  { id: "b2b", label: "B2B bookings" },
  { id: "exceptional", label: "Exceptional circumstances" },
  { id: "howto", label: "How to request a refund" },
  { id: "fairness", label: "Fairness & compliance" },
  { id: "contact", label: "Contact" },
];

const REFUND_BANDS = [
  { window: "21+ days before", refund: "100% of balance", notes: "Deposit retained; custom costs deducted if incurred." },
  { window: "14–20 days", refund: "50% of balance", notes: "Deposit retained; custom costs deducted if incurred." },
  { window: "7–13 days", refund: "25% of balance", notes: "Deposit retained; custom costs deducted if incurred." },
  { window: "0–6 days", refund: "0%", notes: "See rescheduling & exceptional circumstances." },
];

function RefundBands() {
  return (
    <>
      {/* Table for md+ screens */}
      <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">When you cancel</th>
              <th className="px-4 py-3 font-semibold">Refund</th>
              <th className="px-4 py-3 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {REFUND_BANDS.map((r) => (
              <tr key={r.window} className="text-gray-800">
                <td className="px-4 py-4">{r.window}</td>
                <td className="px-4 py-4 font-medium">{r.refund}</td>
                <td className="px-4 py-4">{r.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards for mobile */}
      <div className="md:hidden grid gap-3">
        {REFUND_BANDS.map((r) => (
          <div key={r.window} className="rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">When</div>
            <div className="text-base font-semibold text-gray-900">{r.window}</div>
            <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">Refund</div>
            <div className="text-base font-semibold text-gray-900">{r.refund}</div>
            <div className="mt-2 text-gray-700">{r.notes}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export default function CancellationRefunds() {
  const location = useLocation();

  return (
    <>
      <Seo
        title="Cancellation & Refunds Policy"
        description="Clear cancellation and refunds policy for Party Crafters event decor bookings in Northern Ireland, including deposits, rescheduling windows, and custom items."
        path={location.pathname}
      />

      {/* Page header */}
      <header className="bg-gradient-to-b from-white to-pink-50/40 border-b border-pink-100/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
                Cancellation & Refunds Policy
              </h1>
              <p className="mt-2 text-gray-600">
                Clear, kind, and compliant terms for your booking.
              </p>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2">
              <span className="inline-flex items-center rounded-full bg-pink-100 text-pink-700 px-3 py-1 text-xs font-medium">
                UK & Northern Ireland
              </span>
              <span className="text-xs text-gray-500">Last updated: 23 Aug 2025</span>
            </div>
          </div>

          {/* At-a-glance summary chips */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">Deposit</div>
              <div className="mt-1 text-gray-900 font-semibold">50% non-refundable once confirmed</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">Rescheduling</div>
              <div className="mt-1 text-gray-900 font-semibold">1 free move &gt;14 days</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">Custom Items</div>
              <div className="mt-1 text-gray-900 font-semibold">Non-refundable once production starts</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content with sticky TOC */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-semibold text-gray-900 mb-2">On this page</div>
              <nav className="space-y-1">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-gray-700 hover:text-pink-600"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-10">
            {/* Summary */}
            <section id="summary" className="scroll-mt-28">
              <div className="rounded-xl border border-pink-200 bg-pink-50 p-5">
                <h2 className="text-xl font-bold text-pink-700">Quick summary</h2>
                <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                  <li>
                    Event décor is a <span className="font-medium">date-specific leisure service</span>, so the usual
                    14-day online cooling-off right generally doesn’t apply.
                  </li>
                  <li>
                    A <span className="font-medium">50% deposit</span> secures your date and is non-refundable once we confirm
                    your booking.
                  </li>
                  <li>
                    Refund bands apply to the remaining balance (see below). Custom/personalised items are
                    non-refundable once production begins.
                  </li>
                </ul>
              </div>
            </section>

            {/* Who */}
            <section id="who" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Who this policy covers</h2>
              <p className="mt-3 text-gray-800">
                This policy applies to consumer bookings in the UK (including Northern Ireland) for
                private events (birthdays, baby showers, etc.). For business/organisation bookings,
                see <a href="#b2b" className="text-pink-600 underline">B2B bookings</a>.
              </p>
            </section>

            {/* Deposits */}
            <section id="deposit" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Booking deposits & confirmation</h2>
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-gray-800">
                  A <span className="font-semibold">non-refundable 50% deposit</span> is required to secure your date and
                  begin planning. Your booking is confirmed when we issue written confirmation after
                  receiving your deposit. The deposit reflects our real costs and the opportunity cost
                  of holding your date — it’s not a penalty.
                </p>
              </div>
            </section>


            {/* Cancellations */}
            <section id="cancellations" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Cancellations by you</h2>
              <p className="mt-3 text-gray-800">
                To cancel, message or email us from the contact details used to book. We’ll confirm in
                writing. Refund bands below apply to the <em>service balance</em> (deposit retained). Custom
                items are non-refundable once production begins. Third-party costs we can’t recover are
                also non-refundable (we’ll share receipts and pass on any supplier refunds we receive).
              </p>
              <div className="mt-5">
                <RefundBands />
              </div>
            </section>

            {/* Rescheduling */}
            <section id="rescheduling" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Rescheduling (moving your date)</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li><span className="font-medium">One free date change</span> if requested more than 14 days before your event (subject to availability and price differences).</li>
                <li>Within 14 days, a <span className="font-medium">£25 re-booking fee</span> may apply to cover admin/restocking.</li>
                <li>Higher-priced dates/packages: pay the difference. Lower-priced: we refund the difference per the bands above.</li>
              </ul>
            </section>

            {/* Changes */}
            <section id="changes" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Changes to your package</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li>Upgrades/add-ons are fine anytime (subject to stock/staffing).</li>
                <li>Downgrades inside 14 days are treated as partial cancellations of removed items and follow the refund bands.</li>
              </ul>
            </section>

            {/* Our side */}
            <section id="ourside" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">If we need to cancel or make a material change</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li>If we cancel: choose a <span className="font-medium">full refund, free date change, or credit</span>. No admin fees.</li>
                <li>If a prop is unavailable, we’ll offer a like-for-like/superior alternative. If you decline and it affects the essence of your booking, you may cancel for a full refund of the affected part (or whole, as appropriate).</li>
              </ul>
            </section>

            {/* Weather */}
            <section id="weather" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Weather, access & safety</h2>
              <p className="mt-3 text-gray-800">
                Outdoor setups need safe, suitable ground and access. If severe weather or unsafe
                conditions prevent the original plan, we’ll attempt a safe alternative layout/location.
                If none exists on the day, it’s treated as a late cancellation unless conditions were
                unforeseeable and extreme — then we apply reasonable discretion.
              </p>
            </section>

            {/* Quality */}
            <section id="quality" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Quality concerns & remedies</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li>Contact us within <span className="font-medium">24 hours</span> with photos/videos if something isn’t right.</li>
                <li>We’ll act proportionately: re-perform (repair/replace), price-reduce, or refund the affected part. If raised during setup, we fix on the spot where possible.</li>
              </ul>
            </section>

            {/* Hire */}
            <section id="hire" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Hire items, loss & damage</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li>Frames, plinths, neon signs, easels and backdrops remain our property from setup to collection.</li>
                <li>Chargeable damage/loss may be invoiced at repair or replacement cost. Normal wear and minor balloon deflation aren’t charged.</li>
              </ul>
            </section>

            {/* B2B */}
            <section id="b2b" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">B2B bookings (business/organisation clients)</h2>
              <p className="mt-3 text-gray-800">
                The same structure applies but under our B2B Terms. Deposit/cancellation bands may vary by scale. Cooling-off rights don’t apply to B2B.
              </p>
            </section>

            {/* Exceptional */}
            <section id="exceptional" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Exceptional circumstances (discretion policy)</h2>
              <p className="mt-3 text-gray-800">
                For serious illness, bereavement, or genuine emergencies, tell us as soon as possible.
                We often offer a credit or date move even inside the usual bands.
              </p>
            </section>

            {/* How to */}
            <section id="howto" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">How to request a cancellation, date change or refund</h2>
              <ol className="mt-3 list-decimal pl-6 text-gray-800 space-y-2">
                <li>Email us with your <span className="font-medium">name</span>, <span className="font-medium">event date</span>, <span className="font-medium">venue/postcode</span>, and <span className="font-medium">booking reference</span> (if you have one).</li>
                <li>Tell us whether you want to cancel, reschedule, or discuss options.</li>
                <li>We reply within <span className="font-medium">2 working days</span> and confirm any refund amount.</li>
              </ol>
              <p className="mt-3 text-gray-800">
                <span className="font-medium">Refund timing:</span> we initiate refunds within 5 working days of written
                confirmation; banks may take a further 3–10 working days to show funds.
              </p>
            </section>

            {/* Fairness */}
            <section id="fairness" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-gray-900">Fairness & compliance</h2>
              <ul className="mt-3 list-disc pl-5 text-gray-800 space-y-2">
                <li>Terms are intended to be clear, fair and reasonable. Deposits/deductions reflect actual costs/commitments; they are not penalties.</li>
                <li>This policy sits alongside your statutory rights under UK consumer law.</li>
              </ul>
            </section>

            {/* Contact */}
            <section id="contact" className="scroll-mt-28">
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900">Contact</h2>
                <p className="mt-3 text-gray-800">
                  <span className="font-semibold">Party Crafters</span><br />
                  Belfast, Northern Ireland<br />
                  <a className="text-pink-600 underline" href="tel:+447762230267">
                    +44 7762 230267
                  </a>
                  <br />
                  Email:{" "}
                  <a className="text-pink-600 underline" href="mailto:partycrafters.events@gmail.com">
                    partycrafters.events@gmail.com
                  </a>
                  
                </p>
              </div>
            </section>
          </article>
        </div>
      </main>
    </>
  );
}
